"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const common_1 = require("../common");
const CreaseVertex_1 = require("./CreaseVertex");
const validEdgeAssignments = {
    U: 'unassigned',
    C: 'cut',
    B: 'boundary',
    M: 'mountain',
    V: 'valley',
    F: 'facet',
};
/**
 * An edge in a Crease object.
 */
class CreaseEdge {
    /**
     * Constructs a CreaseEdge object.
     * @param {number} index The index of this edge.
     * @param {CreaseVertex} vertex1 The first vertex of this edge.
     * @param {CreaseVertex} vertex2 The second vertex of this edge.
     * @param {CreaseEdgeAssignment} assignment The edge's assignment.
     * @param {Vector2[]} controlPoints An array of 2D curve control points.
     * @param {number | null} foldAngle The edge's fold angle (in degrees).
     */
    constructor(index, vertex1, vertex2, assignment = null, controlPoints = [], foldAngle = null) {
        // Check inputs.
        common_1.check.isInteger('index', index);
        common_1.check.isInstanceOf('vertex1', vertex1, CreaseVertex_1.CreaseVertex);
        common_1.check.isInstanceOf('vertex2', vertex2, CreaseVertex_1.CreaseVertex);
        if (assignment !== null) {
            common_1.check.isAmong('assignment', assignment, Object.keys(validEdgeAssignments));
        }
        common_1.check.isArray('controlPoints', controlPoints, [0, 2], common_1.check.is2DPoint);
        if (assignment === 'F') {
            if (foldAngle !== 0) {
                throw new Error('Edges of type F must have 0 fold angle.');
            }
        }
        else if (assignment === 'C' || assignment === 'B') {
            if (foldAngle !== null) {
                throw new Error('Edges of type C or B must have null fold angle.');
            }
        }
        else if (foldAngle !== null) {
            common_1.check.isInRange('foldAngle', foldAngle, 0, 180);
        }
        this._index = index;
        this._vertex1 = vertex1;
        this._vertex2 = vertex2;
        this._assignment = assignment;
        this._controlPoints = controlPoints;
        this._foldAngle = foldAngle;
    }
    /**
     * Returns the index of this edge.
     * @returns {number} The index of this edge.
     */
    get index() {
        return this._index;
    }
    /**
     * Returns the first vertex of this edge.
     * @returns {CreaseVertex} The first vertex of this edge.
     */
    get vertex1() {
        return this._vertex1;
    }
    /**
     * Returns the second vertex of this edge.
     * @returns {CreaseVertex} The second vertex of this edge.
     */
    get vertex2() {
        return this._vertex2;
    }
    /**
     * Returns the face to the left of this edge.
     * @returns {CreaseFace | undefined} The face to the left of this edge.
     */
    get leftFace() {
        return this._leftFace;
    }
    /**
     * Returns the face to the right of this edge.
     * @returns {CreaseFace | undefined} The face to the right of this edge (or undefined, for a
     * boundary edge).
     */
    get rightFace() {
        return this._rightFace;
    }
    /**
     * Returns true if this is a boundary edge, meaning it has no face to its right.
     * @returns {boolean} True if this is a boundary edge; false otherwise.
     */
    get isBoundary() {
        return !this.rightFace;
    }
    /**
     * Returns the length of the edge.
     * @returns {number} Edge length.
     */
    get length() {
        return common_1.vec.distance(this.vertex1.position, this.vertex2.position);
    }
    /**
     * Returns the assignment of this edge.
     * @returns {CreaseEdgeAssignment} The assignment of this edge.
     */
    get assignment() {
        return this._assignment;
    }
    /**
     * Sets the assignment of this edge.
     * @param {CreaseEdgeAssignment} assignment The edge assignment as a string.
     */
    set assignment(assignment) {
        if (assignment !== null) {
            common_1.check.isAmong('assignment', assignment, Object.keys(validEdgeAssignments));
        }
        this._assignment = assignment;
        if (assignment === 'B' || assignment === 'C') {
            this._foldAngle = null;
        }
    }
    /**
     * Returns true if this is a crease edge, meaning its assignment is 'U', 'M', or 'V'.
     * @returns {boolean} True if this is a crease edge; false otherwise.
     */
    get isCrease() {
        return !!this.assignment && ['U', 'M', 'V'].indexOf(this.assignment) >= 0;
    }
    /**
     * Returns an array of 2D curve control points for this edge.
     * @returns {Vector2[]} An array of 2D curve control points for this edge.
     */
    get controlPoints() {
        return this._controlPoints;
    }
    /**
     * Returns the fold angle of this edge (in degrees, between 0 and 180), or null.
     * @returns {number | null} The fold angle of this edge.
     */
    get foldAngle() {
        return this._foldAngle;
    }
    /**
     * Sets the fold angle of this edge.
     * @param {number | null} foldAngle A fold angle (in degrees, between 0 and 180), or null.
     */
    set foldAngle(foldAngle) {
        if (foldAngle !== null) {
            common_1.check.isInRange('foldAngle', foldAngle, 0, 180);
        }
        this._foldAngle = foldAngle;
    }
    /**
     * Returns an array containing all the points defining this edge, including the first vertex
     * position, the curve control points, and the second vertex position.
     * @returns {Bezier2} An array of all points defining this edge.
     */
    get allPoints() {
        return [this.vertex1.position, ...this.controlPoints, this.vertex2.position];
    }
    /**
     * Returns the vertex opposite the specified vertex in this edge.
     * @param {CreaseVertex} v One of the vertices of this edge.
     * @returns {CreaseVertex} The vertex opposite the specified vertex in this edge.
     */
    getOtherVertex(v) {
        if (v !== this.vertex1 && v !== this.vertex2) {
            throw new Error(`Vertex ${v.index} is not adjacent to edge ${this.index}.`);
        }
        return this.vertex1 === v ? this.vertex2 : this.vertex1;
    }
    /**
     * Reverses the direction of this edge. Used internally.
     */
    _reverse() {
        const tempFace = this._leftFace;
        this._leftFace = this._rightFace;
        this._rightFace = tempFace;
        const tempVertex = this._vertex1;
        this._vertex1 = this._vertex2;
        this._vertex2 = tempVertex;
        this._controlPoints.reverse();
    }
    /**
     * Replaces either vertex1 or vertex2 (whichever matches the old vertex) with a new vertex.
     * Used internally by `CreaseBuilder._removeRedundantVertices`.
     * @param oldVertex The existing vertex to be replaced.
     * @param newVertex The new vertex to use as the replacement.
     */
    _replaceVertex(oldVertex, newVertex) {
        if (this._vertex1 === oldVertex) {
            this._vertex1 = newVertex;
        }
        else if (this._vertex2 === oldVertex) {
            this._vertex2 = newVertex;
        }
        else {
            throw new Error('Cannot find vertex that needs replacement.');
        }
    }
    /**
     * Replaces the control points of this edge. Used internally.
     * @param controlPoints The new control points.
     */
    _setControlPoints(controlPoints) {
        this._controlPoints = controlPoints;
    }
    /**
     * Assigns a new index to this edge. Used internally.
     * @param index The new index for this edge.
     */
    _setIndex(index) {
        this._index = index;
    }
    /**
     * Returns an object that can be serialized using `JSON.stringify`.
     * @returns {CreaseJSONEdge} The serializable representation of this edge.
     */
    toJSON() {
        const json = {
            vertex1: this.vertex1.index,
            vertex2: this.vertex2.index,
        };
        if (this.assignment) {
            json.assignment = this.assignment;
        }
        if (this.controlPoints.length) {
            json.controlPoints = this.controlPoints;
        }
        if (this.foldAngle !== undefined && this.foldAngle !== null) {
            json.foldAngle = this.foldAngle;
        }
        return json;
    }
    /**
     * Checks deep equality between this and another CreaseEdge object
     * @param {CreaseEdge} edge An edge.
     * @param {boolean} [logDebug = false] A flag to log unequal elements, default to false.
     * @param {number} [epsilon = 1e-2] A small epsilon to use to compare floating point values.
     * @returns {boolean} Returns true if equal, else false.
     */
    equals(edge, logDebug = false, epsilon = 1e-2) {
        let equal = true;
        const { vertex1, vertex2, foldAngle, index } = this;
        if (edge.vertex1.index !== vertex1.index || edge.vertex2.index !== vertex2.index) {
            if (logDebug) {
                /* eslint-disable-next-line no-console */
                console.log(`Edges at index ${index} have unequal vertices: ${JSON.stringify([vertex1.index, vertex2.index])},
                ${JSON.stringify([edge.vertex1.index, edge.vertex2.index])}`);
            }
            equal = false;
        }
        if (((edge.foldAngle === null) !== (foldAngle === null))
            || (edge.foldAngle !== null && foldAngle !== null
                && Math.abs(edge.foldAngle - foldAngle) > epsilon)) {
            if (logDebug) {
                /* eslint-disable-next-line no-console */
                console.log(`Edges at index ${index} have unequal foldAngles: ${foldAngle}, ${edge.foldAngle}`);
            }
            equal = false;
        }
        // We could add more here if needed.
        return equal;
    }
}
exports.CreaseEdge = CreaseEdge;
//# sourceMappingURL=CreaseEdge.js.map